import { AxiosInstance } from 'axios';
import type { components, operations } from '@/api/generated/openapi';
import { axiosWithoutCaseMiddleware, fetchPaginatedDataFunctionWithMapper } from '@/helpers/forms/axios';
import { DataFetcherFunction } from '../../compiler/types';

type GeneratedListPrintJobsResponse = operations['index-print-jobs']['responses']['200']['content']['application/json'];
type GeneratedPrintJob = GeneratedListPrintJobsResponse['data'][number];

export type PrinterRaw = components['schemas']['Printer'];
export type PrintDocumentRaw = components['schemas']['PrintDocument'];

export type PrintJobRaw = GeneratedPrintJob;

export type ListPrintJobsResponse = GeneratedListPrintJobsResponse;
export type AttemptPrintJobResponse = operations['attempt-print-job']['responses']['200']['content']['application/json'];
export type FailPrintJobResponse = operations['fail-print-job']['responses']['200']['content']['application/json'];
export type CancelPrintJobResponse = operations['cancel-print-job']['responses']['200']['content']['application/json'];
export type CompletePrintJobResponse = operations['complete-print-job']['responses']['200']['content']['application/json'];

export type FailPrintJobRequest = operations['fail-print-job']['requestBody']['content']['application/json'];
export type CancelPrintJobRequest = NonNullable<operations['cancel-print-job']['requestBody']>['content']['application/json'];

// The queue-creation operation is not yet included in the published OpenAPI bundle.
export interface CreatePrintJobRequest {
    name: string
    document: { url: string; width_mm: number; height_mm: number; type?: 'pdf' }
    printer: { identifier: string; brand?: string | null; density?: number | null; orientation?: 'auto' | 'landscape' | 'portrait' | null }
    count?: 1
}

export interface CreatedPrintJob {
    id: string
    status: 'waiting' | 'completed' | 'cancelled'
}

export interface PrintJobEventRecord {
    id: string
    event: 'print_job_failed' | 'print_job_completed' | 'print_job_cancelled' | 'print_job_attempted'
    occurredAt: string
    data: {
        failureReason?: string
        cancellationReason?: string
    }
}

export interface PrintJob {
    id: string
    createdAt: string
    updatedAt: string
    status: 'waiting' | 'completed' | 'cancelled'
    name: string
    source: string
    printer: {
        identifier: string
        brand: string | null
        density: number | null
        orientation?: 'auto' | 'landscape' | 'portrait' | null
    }
    events: PrintJobEventRecord[]
}

// The attempt response carries the document but not the event history.
export type DetailedPrintJob = Omit<PrintJob, 'events'> & {
    document: {
        url: string
        heightMm: number
        widthMm: number
        type: 'pdf' | 'zpl' | 'epl'
    }
};

const toPrintJobEvent = (event: PrintJobRaw['events'][number]): PrintJobEventRecord => {
    // The server serialises an empty event payload as `[]`.
    const data = Array.isArray(event.data) ? {} : event.data;

    return {
        id: event.id,
        event: event.event,
        occurredAt: event.occurred_at,
        data: {
            failureReason: data.failure_reason,
            cancellationReason: data.cancellation_reason,
        },
    };
};

const toPrinter = (printer: PrinterRaw): PrintJob['printer'] => ({
    identifier: printer.identifier,
    brand: printer.brand,
    density: printer.density,
    orientation: printer.orientation,
});

const toPrintDocument = (document: PrintDocumentRaw): DetailedPrintJob['document'] => ({
    url: document.url,
    heightMm: document.height_mm,
    widthMm: document.width_mm,
    type: document.type,
});

const toPrintJob = (job: PrintJobRaw): PrintJob => ({
    id: job.id,
    createdAt: job.created_at,
    updatedAt: job.updated_at,
    status: job.status,
    name: job.name,
    source: job.source,
    printer: toPrinter(job.printer),
    events: job.events.map(toPrintJobEvent),
});

const toDetailedPrintJob = (job: AttemptPrintJobResponse): DetailedPrintJob => ({
    id: job.id,
    createdAt: job.created_at,
    updatedAt: job.updated_at,
    status: job.status,
    name: job.name,
    source: job.source,
    printer: toPrinter(job.printer),
    document: toPrintDocument(job.document),
});

// Thrown when an `attempt` request for a job is already in flight. The backend serializes
// print-job actions behind a per-job lock, so a concurrent duplicate would lose the race and
// return a phantom 409. We drop the duplicate here so only the winning attempt reaches the
// server and drives the print — callers treat this as a silent skip, not a failure.
export class PrintAttemptInFlightError extends Error {
    constructor(printJobId: string) {
        super(`A print attempt for job ${printJobId} is already in flight`);
        this.name = 'PrintAttemptInFlightError';
    }
}

const inFlightAttempts = new Set<string>();

// The dedupe guard only needs to cover the brief window in which a concurrent duplicate could
// fire. This timeout bounds that window so a stalled request cannot leave a job id stuck in the
// set — which would otherwise silently skip every retry until the page reloaded.
const attemptTimeoutMs = 30000;

export default (axios: AxiosInstance = axiosWithoutCaseMiddleware): { createPrintJob: (request: CreatePrintJobRequest) => Promise<CreatedPrintJob>; fetchPrintJobs: DataFetcherFunction<PrintJob>; attemptPrint: (printJobId: string) => Promise<DetailedPrintJob>; printFailed: (printJobId: string, failureReason: string) => Promise<void>; cancelPrint: (printJobId: string, cancellationReason: string) => Promise<void>; completePrint: (printJobId: string) => Promise<void>; } => ({
    createPrintJob: async (request: CreatePrintJobRequest): Promise<CreatedPrintJob> => {
        const response = await axios.post<{ data: CreatedPrintJob }>('/api/printing/jobs', request);
        return response.data.data;
    },
    fetchPrintJobs: fetchPaginatedDataFunctionWithMapper<ListPrintJobsResponse['data'][number], PrintJob>('/api/printing/jobs', toPrintJob, undefined, axios),

    attemptPrint: async (printJobId: string): Promise<DetailedPrintJob> => {
        if (inFlightAttempts.has(printJobId)) {
            throw new PrintAttemptInFlightError(printJobId);
        }

        inFlightAttempts.add(printJobId);

        try {
            const response = await axios.post<AttemptPrintJobResponse>(`/api/printing/jobs/${printJobId}/attempt`, undefined, { timeout: attemptTimeoutMs });

            return toDetailedPrintJob(response.data);
        } finally {
            inFlightAttempts.delete(printJobId);
        }
    },

    printFailed: async (printJobId: string, failureReason: string): Promise<void> => {
        const body: FailPrintJobRequest = { failure_reason: failureReason };

        await axios.post<FailPrintJobResponse>(`/api/printing/jobs/${printJobId}/failed`, body);
    },

    cancelPrint: async (printJobId: string, cancellationReason: string): Promise<void> => {
        const body: CancelPrintJobRequest = { cancellation_reason: cancellationReason };

        await axios.post<CancelPrintJobResponse>(`/api/printing/jobs/${printJobId}/cancel`, body);
    },

    completePrint: async (printJobId: string): Promise<void> => {
        await axios.post<CompletePrintJobResponse>(`/api/printing/jobs/${printJobId}/complete`);
    },
});
